import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Mail, Phone, ArrowRight } from "lucide-react";
import { useI18n } from "../i18n";
import api from "../api";
import { LLC, isLLCFormed } from "../legal";

export default function About() {
  const { lang } = useI18n();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const C = COPY[lang];

  return (
    <div data-testid="about-page">
      <section className="container-cd pt-16 pb-12" style={{ borderBottom: "1px solid var(--line)" }}>
        <div className="overline">{C.kicker}</div>
        <h1 className="font-display text-5xl md:text-7xl mt-4 leading-[0.9] max-w-4xl">
          {C.title}
        </h1>
        <p className="mt-6 max-w-3xl text-base md:text-lg" style={{ color: "var(--ink-2)" }}>
          {C.sub}
        </p>
      </section>

      <section className="container-cd py-16 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="overline">{C.founderLabel}</div>
          <div className="mt-4 w-32 h-32 flex items-center justify-center font-display text-5xl" style={{ background: "var(--orange)", color: "white" }}>
            DV
          </div>
          <div className="font-display text-2xl mt-4">Derick Varela</div>
          <div className="font-mono text-xs mt-1" style={{ color: "var(--ink-2)" }}>
            {C.founderRole}
          </div>
          <div className="mt-4 space-y-2 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
            <div className="flex items-center gap-2"><MapPin size={12} /> Las Cruces, NM · 45 min de Juárez</div>
            <div className="flex items-center gap-2"><Mail size={12} /> {LLC.founder_email}</div>
            <div className="flex items-center gap-2"><Phone size={12} /> WhatsApp on request</div>
          </div>
        </div>

        <div className="md:col-span-8 space-y-8">
          {C.body.map((para, i) => (
            <p key={i} className="text-base md:text-lg leading-relaxed" style={{ color: "var(--ink)" }}>
              {para}
            </p>
          ))}
        </div>
      </section>

      <section className="container-cd py-16" style={{ borderTop: "1px solid var(--line)" }}>
        <div className="overline">{C.numbersKicker}</div>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          <Stat v={stats?.manufacturers_listed ?? "—"} k={C.s.listed} />
          <Stat v={stats?.founding_spots_claimed ?? "—"} k={C.s.founding} />
          <Stat v={stats?.total_documents_secured ?? "—"} k={C.s.docs} />
          <Stat v={stats?.total_inquiries ?? "—"} k={C.s.inq} />
        </div>
      </section>

      <section className="container-cd py-16" style={{ borderTop: "1px solid var(--line)" }}>
        <div className="overline">{C.entityKicker}</div>
        <div className="mt-6 cd-card p-6 max-w-2xl">
          <div className="font-display text-2xl">
            {isLLCFormed ? LLC.name : LLC.pending_label}
          </div>
          <div className="mt-3 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
            {LLC.registered_address_line_1}
            <br />
            {LLC.registered_address_line_2}
          </div>
          <div className="mt-3 font-mono text-xs">
            <a className="underline" href={`mailto:${LLC.contact_email}`}>{LLC.contact_email}</a> ·{" "}
            <a className="underline" href={`mailto:${LLC.support_email}`}>{LLC.support_email}</a>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 font-mono text-xs">
            <Link to="/legal/terms" className="cd-chip cd-chip-interactive">{C.terms}</Link>
            <Link to="/legal/privacy" className="cd-chip cd-chip-interactive">{C.privacy}</Link>
            <Link to="/legal/refund" className="cd-chip cd-chip-interactive">{C.refund}</Link>
          </div>
        </div>
      </section>

      <section className="py-16" style={{ background: "var(--ink)", color: "white" }}>
        <div className="container-cd grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="overline" style={{ color: "var(--orange)" }}>{C.ctaKicker}</div>
            <h2 className="font-display text-3xl md:text-5xl mt-3 leading-tight">{C.ctaTitle}</h2>
          </div>
          <div className="md:col-span-5 flex flex-col gap-3">
            <Link to="/register" className="btn btn-primary w-full justify-center" data-testid="about-cta-register">
              {C.ctaList} <ArrowRight size={14} />
            </Link>
            <Link to="/sales-kit" className="btn btn-ghost w-full justify-center" style={{ color: "white", borderColor: "rgba(255,255,255,0.4)" }} data-testid="about-cta-saleskit">
              {C.ctaKit}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ v, k }) {
  return (
    <div className="cd-card p-6">
      <div className="font-display text-4xl md:text-5xl">{v}</div>
      <div className="overline mt-2" style={{ color: "var(--ink-2)" }}>{k}</div>
    </div>
  );
}

const COPY = {
  en: {
    kicker: "ABOUT · CORRIDOR DIGITAL",
    title: "We built the registry the corridor was missing.",
    sub: "$2.3B in US-Mexico trade flows through Juárez–El Paso every day. The plants that build that economy have been invisible online. That ends here.",
    founderLabel: "FOUNDER",
    founderRole: "Founder · Corridor Digital · Apex Digital",
    body: [
      "I'm Derick. I live in Las Cruces, New Mexico — 45 minutes north of Ciudad Juárez. I've spent the last decade watching the same pattern: a US procurement manager in Detroit or Phoenix types \"CNC machining Juárez\" into Google and gets nothing useful. Meanwhile, 330+ IMMEX plants two highway exits south can fulfill that exact RFQ. They just have no English digital presence.",
      "Corridor Digital is the fix. Not a marketplace. Not a middleman. A bilingual directory of record — IMMEX plants on one side, US buyers on the other, with a compliance vault and live document-expiration dashboard in between. The factories own their data. The buyers verify before flying down. Nobody is brokered.",
      "We're starting with 10 founding members. We come to the plant. We photograph it. We translate the documents. We write the bilingual profile. In six weeks the factory has a real US-buyer-ready digital presence — and a permanent corridor advantage.",
      "If you build things in Juárez, Chihuahua, or anywhere on the corridor — list your plant. If you buy from Mexico — browse the directory. If you want to know more — email me.",
    ],
    numbersKicker: "THE CORRIDOR · BY THE NUMBERS",
    s: { listed: "Plants listed", founding: "Founding spots claimed", docs: "Docs secured", inq: "RFQs received" },
    entityKicker: "OPERATING ENTITY",
    terms: "Terms",
    privacy: "Privacy",
    refund: "Refund Policy",
    ctaKicker: "TWO WAYS IN",
    ctaTitle: "List your plant. Or browse the directory. Either gets you in motion.",
    ctaList: "List your plant",
    ctaKit: "Read the sales kit",
  },
  es: {
    kicker: "ACERCA · CORRIDOR DIGITAL",
    title: "Construimos el registro que le faltaba al corredor.",
    sub: "$2.3B en comercio EE.UU.–México pasan por Juárez–El Paso cada día. Las plantas que construyen esa economía han sido invisibles en línea. Eso termina aquí.",
    founderLabel: "FUNDADOR",
    founderRole: "Fundador · Corridor Digital · Apex Digital",
    body: [
      "Soy Derick. Vivo en Las Cruces, Nuevo México — 45 minutos al norte de Ciudad Juárez. Llevo una década viendo el mismo patrón: un gerente de compras en Detroit o Phoenix busca \"maquinado CNC Juárez\" en Google y no encuentra nada útil. Mientras tanto, 330+ plantas IMMEX a dos salidas de autopista al sur pueden fabricar exactamente esa RFQ. Solo que no tienen presencia digital en inglés.",
      "Corridor Digital es la solución. No es un marketplace. No es un intermediario. Es un directorio bilingüe oficial — plantas IMMEX de un lado, compradores estadounidenses del otro, con una bóveda de cumplimiento y un panel de vencimientos en vivo en medio. Las fábricas son dueñas de sus datos. Los compradores verifican antes de viajar. Nadie hace de bróker.",
      "Empezamos con 10 miembros fundadores. Vamos a la planta. La fotografiamos. Traducimos los documentos. Redactamos el perfil bilingüe. En seis semanas la fábrica tiene una presencia digital real lista para compradores en EE.UU. — y una ventaja permanente en el corredor.",
      "Si fabricas en Juárez, Chihuahua o en cualquier punto del corredor — lista tu planta. Si compras desde México — explora el directorio. Si quieres saber más — escríbeme.",
    ],
    numbersKicker: "EL CORREDOR · EN NÚMEROS",
    s: { listed: "Plantas listadas", founding: "Lugares fundadores", docs: "Documentos asegurados", inq: "RFQs recibidos" },
    entityKicker: "ENTIDAD OPERADORA",
    terms: "Términos",
    privacy: "Privacidad",
    refund: "Reembolsos",
    ctaKicker: "DOS FORMAS DE ENTRAR",
    ctaTitle: "Lista tu planta. O explora el directorio. Cualquiera te pone en marcha.",
    ctaList: "Lista tu planta",
    ctaKit: "Leer el kit de ventas",
  },
};
